import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMembershipTypes, getCurrentMembership, checkUpgradeEligibility } from '../services/api';
import PlanCard from '../components/membership/PlanCard';
import MembershipComparisonTable from '../components/membership/MembershipComparisonTable';
import Alert from '../components/common/Alert';

function Membership() {
  const navigate = useNavigate();
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [currentMembership, setCurrentMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showComparison, setShowComparison] = useState(false);
  const [upgradeEligible, setUpgradeEligible] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch membership types
        const typesData = await getMembershipTypes();
        setMembershipTypes(typesData.results || typesData);
        
        // Try to fetch current membership
        try {
          const membershipData = await getCurrentMembership();
          setCurrentMembership(membershipData);
        } catch (error) {
          // No active membership is not an error
          console.log('No active membership found');
        }
      } catch (error) {
        setError('Failed to load membership data. Please try again.');
        console.error('Error loading memberships:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Check upgrade eligibility whenever membership changes
  useEffect(() => {
    const checkUpgrade = async () => {
      if (currentMembership && currentMembership.membership_type.name === 'Pro Member - Quarterly') {
        try {
          const eligibilityData = await checkUpgradeEligibility();
          if (eligibilityData.eligible) {
            setUpgradeEligible(true);
          }
        } catch (error) {
          console.log('User not eligible for upgrade:', error);
          setUpgradeEligible(false);
        }
      } else {
        setUpgradeEligible(false);
      }
    };

    checkUpgrade();
  }, [currentMembership]);

  // Organize plans by type
  const communityPlan = membershipTypes.find(type => type.name === 'Community Member');
  const proPlan = membershipTypes.filter(type => type.name.startsWith('Pro Member'));
  
  // Sort Pro plans: Quarterly first, then Annual
  proPlan.sort((a, b) => a.duration_months - b.duration_months);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="max-w-md">
          <Alert type="error" message={error} onClose={() => setError('')} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
              Choose Your Membership
            </h1>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              Join our community and unlock access to exclusive content, events, and resources. 
              Start your journey with us today.
            </p>
          </div>
        </div>
      </div>

      {/* Subscription Management Section - Show for all users */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Subscription Management</h2>
          
          {/* Current Membership Status */}
          {currentMembership ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-base font-medium text-blue-800">
                    Current Plan: {currentMembership.membership_type.name}
                  </h3>
                  {parseFloat(currentMembership.membership_type.price) > 0 && currentMembership.current_period_end && (
                    <p className="text-sm text-blue-600">
                      {currentMembership.auto_renew ? 'Next billing: ' : 'Expires: '}
                      {new Date(currentMembership.current_period_end + 'T00:00:00').toLocaleDateString()}
                      {currentMembership.days_until_expiry !== null && (
                        <span> ({currentMembership.days_until_expiry} days remaining)</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 19c7 0 9-4 9-9s-2-9-9-9-9 4-9 9 2 9 9 9z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-base font-medium text-gray-800">
                    No Active Membership
                  </h3>
                  <p className="text-sm text-gray-600">
                    Choose a plan below to get started
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Upgrade/Change Plan */}
            <div className="bg-gray-50 rounded-lg p-4 text-center min-h-[160px] flex flex-col justify-between">
              <div>
                <div className={`${
                  upgradeEligible ? 'bg-blue-100' : currentMembership && currentMembership.membership_type.name === 'Pro Member - Annual' ? 'bg-gray-100' : 'bg-blue-100'
                } rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center`}>
                  <svg className={`w-6 h-6 ${
                    upgradeEligible ? 'text-blue-600' : currentMembership && currentMembership.membership_type.name === 'Pro Member - Annual' ? 'text-gray-400' : 'text-blue-600'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-900 mb-2">
                  {upgradeEligible ? 'Upgrade to Annual' : currentMembership ? 'Change Plan' : 'Get Started'}
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  {upgradeEligible
                    ? 'Save money with annual billing'
                    : currentMembership && currentMembership.membership_type.name === 'Pro Member - Annual'
                    ? 'You are on the best plan'
                    : currentMembership
                    ? 'Upgrade or change your membership'
                    : 'Choose your first membership plan'}
                </p>
              </div>
              <button
                onClick={() => {
                  if (upgradeEligible) {
                    // Navigate to upgrade page for quarterly→annual upgrade
                    navigate('/upgrade');
                  } else if (currentMembership && currentMembership.membership_type.name === 'Community Member') {
                    // Upgrade to Pro
                    navigate('/upgrade');
                  } else if (currentMembership && currentMembership.membership_type.name !== 'Pro Member - Annual') {
                    // Show plans section below
                    document.getElementById('membership-plans').scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                disabled={currentMembership && currentMembership.membership_type.name === 'Pro Member - Annual'}
                className={`w-full px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 ${
                  currentMembership && currentMembership.membership_type.name === 'Pro Member - Annual'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
                }`}
              >
                {upgradeEligible
                  ? 'Upgrade Now'
                  : currentMembership && currentMembership.membership_type.name === 'Pro Member - Annual'
                  ? 'Already on Best Plan'
                  : currentMembership && currentMembership.membership_type.name === 'Community Member'
                  ? 'Upgrade Now'
                  : 'View Plans'}
              </button>
            </div>

            {/* Payment History */}
            <div className="bg-gray-50 rounded-lg p-4 text-center min-h-[160px] flex flex-col justify-between">
              <div>
                <div className="bg-green-100 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-900 mb-2">Payment History</h3>
                <p className="text-sm text-gray-600 mb-3">
                  {currentMembership ? 'View your payment transactions' : 'No payment history yet'}
                </p>
              </div>
              <button 
                onClick={() => {
                  if (currentMembership) {
                    navigate('/billing-history');
                  } else {
                    alert('You need an active subscription to view payment history.');
                  }
                }}
                className={`w-full px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 ${
                  currentMembership 
                    ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                disabled={!currentMembership}
              >
                View History
              </button>
            </div>

            {/* Billing Management */}
            <div className="bg-gray-50 rounded-lg p-4 text-center min-h-[160px] flex flex-col justify-between">
              <div>
                <div className="bg-yellow-100 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-900 mb-2">Billing Settings</h3>
                <p className="text-sm text-gray-600 mb-3">
                  {currentMembership && parseFloat(currentMembership.membership_type.price) > 0 
                    ? 'Update payment method and billing info' 
                    : 'Billing settings available after subscription'}
                </p>
              </div>
              <button
                onClick={() => {
                  if (currentMembership && parseFloat(currentMembership.membership_type.price) > 0) {
                    navigate('/billing-settings');
                  } else {
                    alert('Billing settings are available after you start a paid subscription.');
                  }
                }}
                className={`w-full px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 ${
                  currentMembership && parseFloat(currentMembership.membership_type.price) > 0
                    ? 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                disabled={!currentMembership || parseFloat(currentMembership.membership_type.price) === 0}
              >
                Manage Billing
              </button>
            </div>

            {/* Cancel/Pause Subscription */}
            <div className="bg-gray-50 rounded-lg p-4 text-center min-h-[160px] flex flex-col justify-between">
              <div>
                <div className="bg-red-100 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-900 mb-2">Cancel Subscription</h3>
                <p className="text-sm text-gray-600 mb-3">
                  {currentMembership && parseFloat(currentMembership.membership_type.price) > 0
                    ? 'Cancel or pause your subscription' 
                    : 'No active paid subscription'}
                </p>
              </div>
              <button
                onClick={() => {
                  if (currentMembership && parseFloat(currentMembership.membership_type.price) > 0) {
                    navigate('/cancel-subscription');
                  } else {
                    alert('You don\'t have an active paid subscription to cancel.');
                  }
                }}
                className={`w-full px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 ${
                  currentMembership && parseFloat(currentMembership.membership_type.price) > 0
                    ? 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                disabled={!currentMembership || parseFloat(currentMembership.membership_type.price) === 0}
              >
                Cancel Plan
              </button>
            </div>
          </div>

          {/* Additional Subscription Details for Paid Plans */}
          {currentMembership && parseFloat(currentMembership.membership_type.price) > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-600">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Billing Type</h4>
                  <p>
                    {currentMembership.auto_renew ? 'Recurring Subscription' : 'One-time Payment'}
                    <br />
                    <span className="text-xs">
                      {currentMembership.membership_type.duration_months === 12 ? 'Annual' : 'Quarterly'} billing
                    </span>
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Auto Renewal</h4>
                  <p className="flex items-center">
                    {currentMembership.auto_renew ? (
                      <>
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        Active
                      </>
                    ) : (
                      <>
                        <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                        Manual renewal required
                      </>
                    )}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Support</h4>
                  <p>
                    <a href="mailto:support@research-triangle.ai" className="text-blue-600 hover:text-blue-800">
                      Contact Support
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Membership Plans */}
      <div id="membership-plans" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Add top padding to accommodate the "Best Value" badge */}
        <div className="pt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
            {/* Community Plan */}
            {communityPlan && (
              <div className="lg:col-span-1 flex">
                <PlanCard
                  plan={communityPlan}
                  currentMembership={currentMembership}
                  isPopular={false}
                  allPlans={membershipTypes}
                />
              </div>
            )}

            {/* Pro Plans */}
            <div className="lg:col-span-2 flex">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                {proPlan.map(plan => (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    currentMembership={currentMembership}
                    isPopular={plan.name === 'Pro Member — Annual'}
                    allPlans={membershipTypes}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Comparison Table Toggle */}
        <div className="mt-16 text-center">
          <button
            onClick={() => setShowComparison(!showComparison)}
            className="inline-flex items-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {showComparison ? 'Hide' : 'Show'} Detailed Comparison
            <svg className={`ml-2 h-5 w-5 transform transition-transform ${showComparison ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Comparison Table */}
        {showComparison && (
          <div className="mt-8">
            <MembershipComparisonTable
              communityPlan={communityPlan}
              proPlan={proPlan}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Membership;